import axios from 'axios';
import i18n from '../i18n';
import { URL_PASSWORD } from '../constants/urls';

export const FETCH_UPDATE_PASSWORD_BEGIN = 'FETCH_UPDATE_PASSWORD_BEGIN';
export const FETCH_UPDATE_PASSWORD_SUCCESS = 'FETCH_UPDATE_PASSWORD_SUCCESS';
export const FETCH_UPDATE_PASSWORD_FAILURE = 'FETCH_UPDATE_PASSWORD_FAILURE';
export const FETCH_UPDATE_PASSWORD_CANCELED = 'FETCH_UPDATE_PASSWORD_CANCELED';
export const FETCH_UPDATE_PASSWORD_REAUTHENTICATION_REQUIRED =
  'FETCH_UPDATE_PASSWORD_REAUTHENTICATION_REQUIRED';
export const PASSWORD_CLEAR_ERROR = 'PASSWORD_CLEAR_ERROR';

// Maps account-api error_codes (sourced from authenticators-api) onto
// the password.change.errors.* i18n keys. Keeps server-emitted English
// strings out of the banner so non-English locales render correctly.
const ERROR_CODE_TO_KEY = {
  PASSWORD_REUSED: 'password.change.errors.reused',
  PASSWORD_TOO_COMMON: 'password.change.errors.tooCommon',
  COMPROMISED_CREDENTIALS: 'password.change.errors.compromised',
  PASSWORD_CONTEXT_SPECIFIC: 'password.change.errors.contextSpecific',
  PASSWORD_REQUIREMENTS_NOT_MET:
    'password.change.errors.doesNotMeetRequirements',
  DOES_NOT_MEET_REQUIREMENTS: 'password.change.errors.doesNotMeetRequirements',
  INVALID_CREDENTIALS: 'password.change.errors.incorrectCurrent',
  TOO_MANY_ATTEMPTS: 'password.change.errors.rateLimited',
  PASSWORD_EMPTY: 'password.change.errors.passwordEmpty',
  PASSWORD_MISSING: 'password.change.errors.passwordMissing',
};

// AXP-1419: the tooCommon / compromised banner copy was reworded to the NIST
// v2 passphrase wording. That copy is rendered by the error_codes -> i18n-key
// localization below, which is shared by both flag states. But that
// localization itself was introduced by #512 (AXP-1297): before #512 these
// strings rendered straight from the server's `data.errors`. With the
// nistPasswordV2 flag OFF the backend is still on v1, so these two codes must
// pass through UNMAPPED here and fall to the pre-#512 `data.errors.flat()`
// path (then the generic key). flag-ON keeps the v2 localization. Every other
// code's copy was never reworded, so only these two are flag-gated.
const FLAG_GATED_CODES = ['PASSWORD_TOO_COMMON', 'COMPROMISED_CREDENTIALS'];

const localizedErrorsFromCodes = (codes, nistPasswordV2) => {
  if (!Array.isArray(codes) || codes.length === 0) return null;
  // AXP-1419: with the flag OFF, leave the reworded codes unmapped so they
  // fall through to the pre-#512 server-copy fallback; flag-ON keeps the v2
  // localization. All other codes keep their localization in both states.
  const keyForCode = (code) =>
    !nistPasswordV2 && FLAG_GATED_CODES.includes(code)
      ? undefined
      : ERROR_CODE_TO_KEY[code];
  // AXP-1297 (Bug 2): authenticators-api can emit the same code more than
  // once (e.g. two validators both report PASSWORD_REUSED), and distinct
  // codes can map to the same localized string. Dedupe the resulting
  // messages so each banner renders exactly once while preserving order.
  const mapped = [
    ...new Set(
      codes
        .map(keyForCode)
        .filter(Boolean)
        .map((key) => i18n.t(key)),
    ),
  ];
  return mapped.length > 0 ? mapped : null;
};

export const updatePassword =
  (passwords, { signal } = {}) =>
  async (dispatch, getState) => {
    dispatch({ type: FETCH_UPDATE_PASSWORD_BEGIN });

    // AXP-1419: read the NIST v2 flag so the tooCommon / compromised banner
    // falls back to the pre-#512 server copy when the flag is off. getState is
    // absent in some unit tests, so default to flag-OFF (matching featureFlags
    // initialState).
    const state = (typeof getState === 'function' && getState()) || {};
    const nistPasswordV2 = !!state.featureFlags?.nistPasswordV2;

    try {
      // AXP-1419: NFC-normalize the new password and its confirmation so the
      // bytes we send match the server's NFC comparison. Leave `current`
      // untouched — it is verified server-side against the stored raw-byte
      // digest, and normalizing it could lock users out.
      const normalized = {
        ...passwords,
        password: passwords.password?.normalize('NFC'),
        confirmation: passwords.confirmation?.normalize('NFC'),
      };
      const response = await axios.put(URL_PASSWORD, normalized, {
        handlesUnauthorized: true,
        signal,
      });
      const data = (response && response.data) || {};

      // AXP-1297 (Bug B): account-api can return HTTP 200 with
      // { success: false } and no `errors` key when the upstream
      // authenticators-api returns an error code that account-api
      // doesn't map. Treat any explicit `success: false` as failure,
      // even when `errors` is absent, and surface a generic message
      // so the UI never silently claims success on a failed update.
      const apiSuccess =
        data.success !== false &&
        !(Array.isArray(data.errors) && data.errors.length > 0);

      if (!apiSuccess) {
        // Prefer localized copy keyed off the structured `error_codes`
        // array. Fall back to the server's English `errors` strings if
        // we don't recognize any code, then to a generic message if the
        // server reported failure with no body at all.
        const localized = localizedErrorsFromCodes(
          data.error_codes,
          nistPasswordV2,
        );
        let payload;
        if (localized) {
          payload = localized;
        } else if (data.errors) {
          payload = data.errors.flat();
        } else {
          payload = [i18n.t('password.change.error_genericFailure')];
        }

        dispatch({
          payload,
          type: FETCH_UPDATE_PASSWORD_FAILURE,
        });
        return false;
      }

      dispatch({
        type: FETCH_UPDATE_PASSWORD_SUCCESS,
      });
      return true;
    } catch (error) {
      const status = error.response?.status;

      if (axios.isCancel(error)) {
        dispatch({ type: FETCH_UPDATE_PASSWORD_CANCELED });
      } else if (status === 401) {
        dispatch({ type: FETCH_UPDATE_PASSWORD_REAUTHENTICATION_REQUIRED });
      } else if (status === 503) {
        dispatch({
          payload: [i18n.t('password.change.errors.unavailable')],
          type: FETCH_UPDATE_PASSWORD_FAILURE,
        });
      } else {
        dispatch({
          payload: error.message,
          type: FETCH_UPDATE_PASSWORD_FAILURE,
        });
      }
      return false;
    }
  };

export const clearError = () => ({
  type: PASSWORD_CLEAR_ERROR,
});
